import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { AutorenewRounded } from '@mui/icons-material';
import { Stack, CircularProgress, Tooltip, IconButton } from '@mui/material';
import { CopyToClipboardButton } from '@ragu-components/ord-components';
import { PasswordInput, useSourceContext, useTranslate } from 'react-admin';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';

import { fromServiceClassToPasswordOptions, generatePassword, validatePassword } from 'src/lib/password';

import type { StackProps } from '@mui/material';
import type { ServiceClassPasswordPolicy } from '@ragu-components/ord-types';
import type { PasswordInputProps } from 'react-admin';

export interface OrdPasswordInputProps extends PasswordInputProps {
  /** Indicates whether the component is in a loading state. */
  isLoading: boolean;
  /** The password policy for generating passwords. */
  passwordPolicy?: ServiceClassPasswordPolicy;
  /** The tooltip to show over the password generation button. */
  tooltip: string;
  /** Whether to initialize the password on mount */
  init?: boolean;
  /** Props to be passed to root Stack component */
  stackProps?: StackProps;
}

export const OrdPasswordInput = ({
  init = true,
  isLoading,
  isRequired,
  passwordPolicy = undefined,
  source,
  tooltip,
  sx,
  stackProps = undefined,
  ...props
}: OrdPasswordInputProps): React.ReactElement => {
  const passwordOptions = useMemo(() => fromServiceClassToPasswordOptions(passwordPolicy), [passwordPolicy]);
  const { setValue, trigger, getValues } = useFormContext();
  const { getSource } = useSourceContext();
  const composedSource = getSource(source);
  const initialized = useRef(false);

  const regeneratePassword = useCallback(() => {
    if (passwordOptions === undefined) return;
    const pwd = generatePassword(passwordOptions);
    setValue(composedSource, pwd, { shouldDirty: true, shouldTouch: true });
  }, [composedSource, passwordOptions, setValue]);

  useEffect(() => {
    if (initialized.current) return;
    if (!init || passwordOptions === undefined) return;
    const currentValue = getValues(composedSource);
    if (currentValue !== undefined && currentValue !== '') {
      initialized.current = true;
      return;
    }
    const generated = generatePassword(passwordOptions);
    setValue(composedSource, generated, { shouldDirty: false, shouldTouch: false });
    initialized.current = true;
  }, [composedSource, init, passwordOptions, setValue, getValues]);

  useEffect(() => {
    if (passwordOptions === undefined) return;
    void trigger(composedSource);
  }, [composedSource, passwordOptions, trigger]);

  return (
    <Stack direction={'row'} alignItems='flex-start' width={'100%'} {...stackProps}>
      <PasswordInput
        source={source}
        isRequired={isRequired}
        autoComplete='new-password'
        sx={{ flexGrow: 1, ...sx }}
        data-testid='password-input-testid'
        {...props}
      />
      <Stack direction={'row'} alignItems='flex-start'>
        {isLoading ? (
          <CircularProgress sx={{ '& .MuiCircularProgress-svg': { width: '24px', height: '24px', padding: '8px' } }} />
        ) : (
          passwordOptions !== undefined && (
            <Tooltip title={tooltip}>
              <IconButton onClick={regeneratePassword}>
                <AutorenewRounded />
              </IconButton>
            </Tooltip>
          )
        )}
        <PasswordClipboardButton source={composedSource} />
        <PasswordValidator source={composedSource} passwordOptions={passwordOptions} isRequired={isRequired} />
      </Stack>
    </Stack>
  );
};

const PasswordClipboardButton = ({ source }: { source: string }): React.ReactElement => {
  const value = useWatch({ name: source }) as string | undefined;
  return <CopyToClipboardButton text={value} />;
};

const PasswordValidator = ({
  source,
  passwordOptions,
  isRequired,
}: {
  source: string;
  passwordOptions?: ReturnType<typeof fromServiceClassToPasswordOptions>;
  isRequired?: boolean;
}): null => {
  const { setError, clearErrors, resetField, getValues, watch } = useFormContext();
  const { submitCount } = useFormState({ name: source });
  const translate = useTranslate();

  // Track last reported error to skip redundant setError/clearErrors calls.
  const lastErrorMessageRef = useRef<string | null>(null);

  // Read value on demand via getValues — no useWatch subscription, no re-renders on keystrokes.
  const validate = useCallback(() => {
    if (passwordOptions === undefined) return;
    const value = getValues(source) as string | undefined;
    const stringValue = typeof value === 'string' ? value : '';
    const trimmedValue = stringValue.trim();

    if (value === '' || value === null) {
      clearErrors(source);
      resetField(source, { defaultValue: undefined });
      lastErrorMessageRef.current = null;
      return;
    }

    let newMessage: string | null = null;
    let errorType: 'required' | 'manual' = 'manual';

    if (isRequired && trimmedValue === '') {
      newMessage = translate('ra.validation.required');
      errorType = 'required';
    } else if (trimmedValue !== '') {
      const error = validatePassword(stringValue, passwordOptions);
      if (error !== undefined) {
        newMessage = translate(error.message, error.args);
      }
    }

    if (newMessage === lastErrorMessageRef.current) return;
    lastErrorMessageRef.current = newMessage;

    if (newMessage === null) {
      clearErrors(source);
    } else {
      setError(source, { type: errorType, message: newMessage });
    }
  }, [clearErrors, getValues, isRequired, passwordOptions, resetField, setError, source, translate]);

  useEffect(() => {
    validate();
    // Subscribe to field changes outside React's render cycle instead of useWatch.
    const { unsubscribe } = watch((_, { name }) => {
      if (name === source || name === undefined) validate();
    });
    return unsubscribe;
  }, [validate, watch, source]);

  useEffect(() => {
    if (submitCount === 0) return;
    validate();
  }, [submitCount, validate]);

  return null;
};
